import React, { useMemo, useRef, useState } from 'react';
import { DialogueDispatcher } from '../dispatch';
import { EffectBus } from '../EffectBus';
import { DialogueContext, DialogueEvent, DialogueEffect } from '../types';
import { ConversationDialogueState, CONVERSATION_STATE_TRANSITIONS, ConversationTrigger } from '../../DialogueManagementConversation/ConversationDialogueBlueprint';
import { TaskDialogueState, TASK_STATE_TRANSITIONS, TaskTrigger } from '../../DialogueManagementTaskClassification/TaskClassificationDialogueBlueprint';
import { MockUIEffectsHandler } from './MockUIEffectsHandler';
import { MockServiceEffectsHandler } from './MockServiceEffectsHandler';
import { SimulatorConversation } from './SimulatorConversation';
import ConversationGraph from './ConversationGraph';
import TaskGraph from './TaskGraph';
import { runAutoTests, TestResult } from './AutoTester';

const initialState: ConversationDialogueState = 'conv_welcome';

function deterministicId(seq: number): string { return `mid_${seq.toString().padStart(4,'0')}`; }
function deterministicNow(epochMs: number, step: number): Date { return new Date(epochMs + step * 1000); }

const SimulatorPage: React.FC = () => {
  const [effectsLog, setEffectsLog] = useState<string[]>([]);
  const [lastEvent, setLastEvent] = useState<DialogueEvent | null>(null);
  const [graphType, setGraphType] = useState<'conversation' | 'task'>('conversation');
  const [stateHistory, setStateHistory] = useState<ConversationDialogueState[]>(['conv_welcome']);
  const seqRef = useRef(0);
  const epochRef = useRef(1730000000000);

  const simulatorConversation = useMemo(() => new SimulatorConversation(), []);

  const uiHandler = useMemo(() => new MockUIEffectsHandler((entry: string) => setEffectsLog(prev => [...prev, entry])), []);
  const serviceHandler = useMemo(() => new MockServiceEffectsHandler(
    (event: DialogueEvent) => dispatcherRef.current?.dispatch(event),
    (entry: string) => setEffectsLog(prev => [...prev, entry])
  ), []);

  const effectBus = useMemo(() => new EffectBus([uiHandler, serviceHandler], { manual: true }), [uiHandler, serviceHandler]);

  const dispatcherRef = useRef<DialogueDispatcher | null>(null);

  const initialContext: DialogueContext = {
    slots: {},
    language: 'en',
    userId: 'sim-user',
    conversationId: 'sim-conv-1'
  };

  if (!dispatcherRef.current) {
    const noop = () => {};
    dispatcherRef.current = new DialogueDispatcher(
      initialState,
      initialContext,
      simulatorConversation,
      noop,
      noop,
      effectBus
    );
  }

  const dispatcher = dispatcherRef.current!;

  function makeUserMessage(text: string) {
    seqRef.current += 1;
    return {
      id: deterministicId(seqRef.current),
      text,
      sender: 'User',
      timestamp: deterministicNow(epochRef.current, seqRef.current),
      isAssistant: false,
      messageType: 'user' as const
    };
  }

  async function sendEvent(event: DialogueEvent) {
    setLastEvent(event);
    await dispatcher.dispatch(event);
    // Process any effects that were generated
    await effectBus.drainAll();
    // Trigger state refresh
    setRefreshTrigger(prev => prev + 1);
  }

  // Get available transitions for current state
  function getAvailableTransitions() {
    const currentConvState = currentState.conversationState;
    const currentTaskState = currentState.context.taskState;
    
    // If we're in task processing and have a task state, show task-level transitions
    if (currentConvState === 'conv_task_processing' && currentTaskState) {
      const taskTransitions = TASK_STATE_TRANSITIONS[currentTaskState] || {};
      
      return Object.entries(taskTransitions).map(([trigger, nextState]) => ({
        trigger: trigger as TaskTrigger,
        nextState: nextState as TaskDialogueState,
        label: trigger.replace(/_/g, ' ').toLowerCase()
      }));
    }
    
    // Otherwise show conversation-level transitions
    const transitions = CONVERSATION_STATE_TRANSITIONS[currentConvState] || {};
    
    return Object.entries(transitions).map(([trigger, nextState]) => ({
      trigger: trigger as ConversationTrigger,
      nextState: nextState as ConversationDialogueState,
      label: trigger.replace('USER_', '').replace(/_/g, ' ').toLowerCase()
    }));
  }

  // Create event from trigger
  function createEventFromTrigger(trigger: ConversationTrigger | TaskTrigger): DialogueEvent {
    switch (trigger) {
      case 'USER_SUBMITS_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SUBMITS_TASK', slots: { task_description: 'mock task' }, confidence: 0.9 };
      case 'USER_CONFIRMS':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_CONFIRMS', slots: {}, confidence: 1 };
      case 'USER_REJECTS':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_REJECTS', slots: {}, confidence: 1 };
      case 'USER_SAYS_HELLO':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SAYS_HELLO', slots: {}, confidence: 1 };
      case 'USER_SAYS_GOODBYE':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SAYS_GOODBYE', slots: {}, confidence: 1 };
      case 'USER_ASKS_FOR_HELP':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_ASKS_FOR_HELP', slots: {}, confidence: 1 };
      case 'USER_SAYS_SOMETHING_ELSE':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SAYS_SOMETHING_ELSE', slots: {}, confidence: 0.7 };
      case 'USER_WANTS_TO_DELETE_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_WANTS_TO_DELETE_TASK', slots: {}, confidence: 0.8 };
      case 'USER_WANTS_TO_CHANGE_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_WANTS_TO_CHANGE_TASK', slots: {}, confidence: 0.8 };
      case 'USER_SAYS_NO':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SAYS_NO', slots: {}, confidence: 1 };
      case 'USER_SUBMITS_TASKS':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_SUBMITS_TASKS', slots: { task_description: 'mock tasks' }, confidence: 0.9 };
      case 'USER_CONFIRMS_TASK_SUBMISSION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_CONFIRMS_TASK_SUBMISSION', slots: {}, confidence: 1 };
      case 'USER_REJECTS_TASK_SUBMISSION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_REJECTS_TASK_SUBMISSION', slots: {}, confidence: 1 };
      case 'USER_WANTS_TO_SUBMIT_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_WANTS_TO_SUBMIT_TASK', slots: {}, confidence: 0.8 };
      case 'USER_RETYPES_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_RETYPES_TASK', slots: { task_description: 'retyped task' }, confidence: 0.9 };
      case 'USER_ACCEPTS_ALL_TASKS':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_ACCEPTS_ALL_TASKS', slots: {}, confidence: 1 };
      case 'USER_CANCELS_DELETION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_CANCELS_DELETION', slots: {}, confidence: 1 };
      case 'USER_CANCELS_CHANGE':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'USER_CANCELS_CHANGE', slots: {}, confidence: 1 };
      // Task triggers
      case 'SELECT_OPTION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'SELECT_OPTION', slots: {}, confidence: 1 };
      case 'SELECT_VARIANT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'SELECT_VARIANT', slots: { variant: 'mock variant' }, confidence: 1 };
      case 'ENTER_AMOUNT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'ENTER_AMOUNT', slots: { amount: '5' }, confidence: 1 };
      case 'ENTER_LOCATION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'ENTER_LOCATION', slots: { location: 'Amsterdam' }, confidence: 1 };
      case 'CONFIRM':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'CONFIRM', slots: { confirmation: true }, confidence: 1 };
      case 'ABORT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'ABORT', slots: {}, confidence: 1 };
      case 'REPHRASE_TASK':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'REPHRASE_TASK', slots: { task_description: 'rephrased task' }, confidence: 1 };
      case 'REPHRASE_VARIANT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'REPHRASE_VARIANT', slots: { variant: 'rephrased variant' }, confidence: 1 };
      case 'INVALID_AMOUNT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'INVALID_AMOUNT', slots: {}, confidence: 1 };
      case 'INVALID_OPTION':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'INVALID_OPTION', slots: {}, confidence: 1 };
      case 'INVALID_VARIANT':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'INVALID_VARIANT', slots: {}, confidence: 1 };
      case 'SOMETHING_ELSE':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'SOMETHING_ELSE', slots: {}, confidence: 1 };
      case 'NO_TASKS_FOUND':
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: 'NO_TASKS_FOUND', slots: {}, confidence: 1 };
      case 'TASK_COMPLETED':
        return { type: 'TASK_COMPLETED', taskData: { id: 'task-1', description: 'Mock task', status: 'completed' } };
      case 'TASK_ABORTED':
        return { type: 'TASK_ABORTED', reason: 'User cancelled' };
      default:
        return { type: 'PARSE_RESULT', messageId: deterministicId(seqRef.current), intentId: trigger, slots: {}, confidence: 0.5 };
    }
  }

  const pending = effectBus.getPendingEffects();
  const [currentState, setCurrentState] = useState(dispatcher.getCurrentState());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [testFailures, setTestFailures] = useState<TestResult[]>([]);

  // Track state changes and update history
  React.useEffect(() => {
    const current = dispatcher.getCurrentState();
    const newState = current.conversationState;
    
    setCurrentState(current);
    
    setStateHistory(prev => {
      const lastState = prev[prev.length - 1];
      if (lastState !== newState) {
        return [...prev, newState];
      }
      return prev;
    });
  }, [refreshTrigger]);

  // Auto-switch to task graph when in task processing
  React.useEffect(() => {
    if (currentState.conversationState === 'conv_task_processing' && currentState.context.taskState) {
      setGraphType('task');
    } else if (currentState.conversationState !== 'conv_task_processing') {
      setGraphType('conversation');
    }
  }, [currentState.conversationState, currentState.context.taskState]);

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'sans-serif' }}>
      <div style={{ flex: 1, borderRight: '1px solid #ddd', padding: 16, overflow: 'auto' }}>
        <h3>Dialogue Graphs</h3>
        <div style={{ marginBottom: 16 }}>
          <button 
            onClick={() => setGraphType('conversation')}
            style={{ 
              marginRight: 8, 
              background: graphType === 'conversation' ? '#2196f3' : '#f5f5f5',
              color: graphType === 'conversation' ? 'white' : 'black',
              border: '1px solid #ccc',
              padding: '8px 16px',
              borderRadius: '4px'
            }}
          >
            Conversation
          </button>
          <button 
            onClick={() => setGraphType('task')}
            style={{ 
              background: graphType === 'task' ? '#4caf50' : '#f5f5f5',
              color: graphType === 'task' ? 'white' : 'black',
              border: '1px solid #ccc',
              padding: '8px 16px',
              borderRadius: '4px'
            }}
          >
            Task
          </button>
          <button 
            onClick={async () => {
              const { results, failures, summary } = await runAutoTests();
              setEffectsLog(prev => [...prev, summary]);
              if (failures.length === 0) {
                setEffectsLog(prev => [...prev, 'All tests passed.']);
              } else {
                setEffectsLog(prev => [...prev, `Failures (${failures.length}):`]);
                failures.slice(0, 50).forEach(r => setEffectsLog(prev => [...prev, `${r.scope} | ${r.fromState} --${r.trigger}--> expected: ${r.expected} | actual: ${r.actual} | effects: ${r.effects}`]));
              }
              setTestFailures(failures);
            }}
            style={{ marginLeft: 8, border: '1px solid #ccc', padding: '8px 16px', borderRadius: '4px' }}
          >
            Run Auto Tests
          </button>
        </div>
        {graphType === 'conversation' ? (
          <ConversationGraph currentState={currentState.conversationState} stateHistory={stateHistory} />
        ) : (
          <TaskGraph currentState={currentState.context.taskState} />
        )}
      </div>
      <div style={{ width: 520, padding: 16, overflow: 'auto' }}>
        <h3>State Walker</h3>
        {testFailures.length > 0 && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Failed tests ({testFailures.length}):</div>
            <ul style={{ maxHeight: 160, overflowY: 'auto', paddingLeft: 18, margin: 0 }}>
              {testFailures.map((f, i) => (
                <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                  {`${f.scope} | ${f.fromState} -- ${f.trigger} → expected: ${String(f.expected)} | actual: ${String(f.actual)} | effects: ${f.effects}`}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>
            Current State: <span style={{ color: '#2196f3' }}>{currentState.conversationState}</span>
            {currentState.context.taskState && (
              <span style={{ color: '#4caf50', marginLeft: '16px' }}>
                Task: {currentState.context.taskState}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, marginBottom: 12 }}>
            Available actions from this state:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {getAvailableTransitions().map((transition, index) => (
              <button
                key={index}
                onClick={async () => {
                  // Uniform flow: always send NEW_MESSAGE and let fixtures produce PARSE_RESULT
                  const userMessage = makeUserMessage(transition.label);
                  await sendEvent({ type: 'NEW_MESSAGE', message: userMessage });
                }}
                style={{
                  background: '#e3f2fd',
                  border: '1px solid #2196f3',
                  color: '#1976d2',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#2196f3';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#e3f2fd';
                  e.currentTarget.style.color = '#1976d2';
                }}
                title={`Action: ${transition.trigger}\nNext State: ${transition.nextState}`}
              >
                {transition.label}
              </button>
            ))}
          </div>
          {getAvailableTransitions().length === 0 && (
            <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
              No available transitions from this state
            </div>
          )}
        </div>

        <h3>Manual Controls</h3>
        <div style={{ marginBottom: 8 }}>
          <input id="msg" placeholder="Type user message" style={{ width: 300, marginRight: 8 }} />
          <button onClick={() => {
            const el = document.getElementById('msg') as HTMLInputElement;
            const text = el.value || '';
            el.value = '';
            if (!text.trim()) return;
            sendEvent({ type: 'NEW_MESSAGE', message: makeUserMessage(text) });
          }}>NEW_MESSAGE</button>
        </div>

        <div style={{ marginBottom: 8 }}>
          <h4>PARSE_RESULT Fixtures:</h4>
          <button onClick={() => sendEvent({ type: 'PARSE_RESULT', messageId: 'x', intentId: 'USER_SAYS_HELLO', slots: {}, confidence: 1 })}>HELLO</button>
          <button onClick={() => sendEvent({ type: 'PARSE_RESULT', messageId: 'x', intentId: 'USER_SAYS_GOODBYE', slots: {}, confidence: 1 })} style={{ marginLeft: 8 }}>GOODBYE</button>
          <button onClick={() => sendEvent({ type: 'PARSE_RESULT', messageId: 'x', intentId: 'USER_SUBMITS_TASK', slots: { task_description: 'mock task' }, confidence: 0.9 })} style={{ marginLeft: 8 }}>SUBMIT_TASK</button>
          <button onClick={() => sendEvent({ type: 'PARSE_RESULT', messageId: 'x', intentId: 'USER_CONFIRMS', slots: {}, confidence: 1 })} style={{ marginLeft: 8 }}>CONFIRM</button>
          <button onClick={() => sendEvent({ type: 'PARSE_RESULT', messageId: 'x', intentId: 'USER_REJECTS', slots: {}, confidence: 1 })} style={{ marginLeft: 8 }}>REJECT</button>
        </div>

        <div style={{ marginBottom: 8 }}>
          <h4>USER_SELECTION:</h4>
          <button onClick={() => sendEvent({ type: 'USER_SELECTION', selection: { type: 'task', value: 'task-1', id: 'task-1' } })}>Select Task</button>
          <button onClick={() => sendEvent({ type: 'USER_SELECTION', selection: { type: 'variant', value: 'variant-1', id: 'variant-1' } })} style={{ marginLeft: 8 }}>Select Variant</button>
          <button onClick={() => sendEvent({ type: 'USER_SELECTION', selection: { type: 'amount', value: 5 } })} style={{ marginLeft: 8 }}>Amount: 5</button>
          <button onClick={() => sendEvent({ type: 'USER_SELECTION', selection: { type: 'location', value: 'Amsterdam' } })} style={{ marginLeft: 8 }}>Location: Amsterdam</button>
        </div>

        <div style={{ marginBottom: 8 }}>
          <h4>System Events:</h4>
          <button onClick={() => sendEvent({ type: 'TASK_COMPLETED', taskData: { id: 'task-1', description: 'Mock task', status: 'completed' } })}>TASK_COMPLETED</button>
          <button onClick={() => sendEvent({ type: 'TASK_ABORTED', reason: 'User cancelled' })} style={{ marginLeft: 8 }}>TASK_ABORTED</button>
          <button onClick={() => sendEvent({ type: 'PARSE_ERROR', messageId: 'x', reason: 'timeout' })} style={{ marginLeft: 8 }}>PARSE_ERROR</button>
        </div>

        <div style={{ marginBottom: 8 }}>
          <h4>Effect Controls:</h4>
          <button onClick={() => effectBus.stepNext()}>Step effects</button>
          <button onClick={() => effectBus.drainAll()} style={{ marginLeft: 8 }}>Run all</button>
          <button onClick={() => {
            setEffectsLog([]);
            setLastEvent(null);
            setStateHistory(['conv_welcome']);
            seqRef.current = 0;
            epochRef.current = 1730000000000;
            // Reset dispatcher to initial state
            const noop = () => {};
            dispatcherRef.current = new DialogueDispatcher(
              initialState,
              initialContext,
              simulatorConversation,
              noop,
              noop,
              effectBus
            );
          }} style={{ marginLeft: 8 }}>RESET</button>
        </div>

        <h3>Readouts</h3>
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <div><b>Conversation state:</b> {currentState.conversationState}</div>
          <div><b>Task state:</b> {currentState.context.taskState || '-'}</div>
          <div><b>Task context:</b> {JSON.stringify(currentState.context.taskContext || {})}</div>
          <div><b>Slots:</b> {JSON.stringify(currentState.context.slots)}</div>
          <div><b>Last event:</b> {lastEvent ? JSON.stringify(lastEvent) : '-'}</div>
        </div>

        <h4>Pending effects</h4>
        <ul style={{ fontSize: 12 }}>
          {pending.map((e: DialogueEffect, idx: number) => (
            <li key={idx}>{e.type}</li>
          ))}
        </ul>

        <h4>Effects log</h4>
        <ul style={{ fontSize: 12 }}>
          {effectsLog.map((l, i) => (<li key={i}>{l}</li>))}
        </ul>
      </div>
    </div>
  );
};

export default SimulatorPage;


